<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useFormatting } from '@gat-ship/utils'
  import {
    cn,
    Modal,
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
    Separator,
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider
  } from '@gat-ship/vue-components'
  import {
    IconAdmin,
    IconBan,
    IconCalendarClock,
    IconClose,
    IconMaximize,
    IconMinimize
  } from '@gat-ship/icons'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'
  import { useTenantUserUtils } from '../../composables/useTenantUserUtils'
  import {
    getTenantUserDetailSidebarItems,
    type TenantUserDetailSection
  } from '../detail-modal/tenant-user-detail-modal-sections'
  import TenantUserSelect from '../inputs/TenantUserSelect.vue'
  import {
    TenantUserDbAccessProvider,
    TenantUserModalProvider,
    TenantUserOwnedDatabaseProvider
  } from '../providers'
  import TenantUserAccessRolesLayout from '../layouts/TenantUserAccessRolesLayout.vue'
  import TenantUserSectionGeneralInfo from '../sections/TenantUserSectionGeneralInfo.vue'
  import TenantUserSectionDatabaseAccess from '../sections/TenantUserSectionDatabaseAccess.vue'
  import TenantUserAccessRolesView from '../../views/TenantUserAccessRolesView.vue'
  import TenantUserDangerZoneView from '../../views/TenantUserDangerZoneView.vue'

  const open = defineModel<boolean>('open', { required: true })
  const activeSection = defineModel<TenantUserDetailSection>('section', {
    required: true
  })

  const emit = defineEmits<{
    (e: 'select', userId: string): void
  }>()

  const { formatDateTime } = useFormatting()
  const { TenantUserUtils } = useTenantUserUtils()

  const service = useTenantUserDetailContext()
  const tenantUser = computed(() => service?.tenantUser.value)

  const sidebarItems = getTenantUserDetailSidebarItems()

  const headerTitle = computed(
    () => tenantUser.value?.fullName ?? 'Tenant User'
  )

  function onSelect(userId: string | undefined) {
    if (userId) {
      emit('select', userId)
    }
  }

  // Viewport-filling mode, toggled from the header next to the close button.
  // Kept across open/close within the session (the modal root stays mounted),
  // but not persisted. Mirrors StakeholderDetailModal.
  const isFullScreen = ref(false)

  // Overrides on top of sidebarMenuButtonVariants to match the user-settings
  // modal styling (auto height, muted inactive text). ring-inset: the sidebar
  // content scrolls, which would clip a ring drawn outside the button bounds.
  const menuButtonClass =
    'text-muted-foreground hover:bg-accent/50 hover:text-foreground data-[active=true]:hover:bg-accent data-[active=true]:hover:text-accent-foreground h-auto cursor-pointer px-3 py-2 focus-visible:ring-inset'

  /** Mobile tab-bar item classes. */
  function tabClass(value: TenantUserDetailSection) {
    return cn(
      'flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
      activeSection.value === value
        ? 'border-primary text-foreground font-medium'
        : 'text-muted-foreground hover:text-foreground hover:border-border border-transparent'
    )
  }
</script>

<template>
  <Modal
    v-model:open="open"
    type="dialog"
    :max-width="1000"
    full-screen-mobile
    hide-close-button
    :full-screen="isFullScreen"
  >
    <SidebarProvider
      :class="
        cn(
          'h-full min-h-0 w-full overflow-hidden',
          isFullScreen ? 'md:h-full' : 'md:h-[min(800px,85vh)] md:rounded-lg'
        )
      "
    >
      <ResizablePanelGroup
        direction="horizontal"
        auto-save-id="tenant-user-modal-sidebar"
      >
        <ResizablePanel
          class="hidden md:block"
          :default-size="22"
          :min-size="18"
          :max-size="40"
        >
          <Sidebar
            collapsible="none"
            class="bg-muted/50 hidden h-full w-full p-3 dark:bg-neutral-800/50 md:flex"
          >
            <SidebarContent class="gap-3">
              <SidebarGroup class="p-0">
                <SidebarGroupContent>
                  <SidebarHeader class="px-1 py-1 text-sm font-medium">
                    Tenant User
                  </SidebarHeader>
                  <TenantUserSelect
                    :model-value="tenantUser?.userId"
                    :pre-selected-user="tenantUser"
                    data-source="internal"
                    required
                    @update:model-value="onSelect"
                  />
                </SidebarGroupContent>
              </SidebarGroup>

              <SidebarGroup class="p-0">
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem
                      v-for="item in sidebarItems"
                      :key="item.key"
                    >
                      <SidebarMenuButton
                        :is-active="activeSection === item.key"
                        :class="menuButtonClass"
                        @click="activeSection = item.key"
                      >
                        <component :is="item.icon" />
                        <span>{{ item.label }}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
              <div v-if="tenantUser" class="mt-auto flex flex-col gap-2 px-1">
                <div
                  v-if="!TenantUserUtils.isActiveTenantUser(tenantUser)"
                  class="bg-background shadow-xs ring-border flex w-full shrink-0 grow-0 flex-col gap-2 self-start rounded-sm p-2.5 text-xs ring-1 ring-inset"
                >
                  <div class="flex flex-wrap items-center gap-2 font-medium">
                    <IconBan class="text-destructive size-4 rounded-full" />
                    <template
                      v-if="
                        TenantUserUtils.isScheduledForActivation(tenantUser)
                      "
                    >
                      To Be Activated
                    </template>
                    <template v-else> Deactivated </template>
                  </div>
                  <Separator />
                  <div class="text-muted-foreground">
                    <span
                      v-if="
                        TenantUserUtils.isScheduledForActivation(tenantUser)
                      "
                    >
                      This user will be activated on
                      {{ formatDateTime(tenantUser.activeFrom) }}.
                    </span>
                    <span v-else> This user is deactivated permanently. </span>
                    <br /><br />
                    You can activate the user and read more about user
                    activation on the
                    <button
                      type="button"
                      class="text-link cursor-pointer"
                      @click="activeSection = 'danger-zone'"
                    >
                      danger zone
                    </button>
                    page.
                  </div>
                </div>
                <div
                  v-if="TenantUserUtils.isScheduledForDeactivation(tenantUser)"
                  class="bg-background shadow-xs ring-border flex w-full shrink-0 grow-0 flex-col gap-2 self-start rounded-sm p-2.5 text-xs ring-1 ring-inset"
                >
                  <div class="flex flex-wrap items-center gap-2 font-medium">
                    <IconCalendarClock
                      class="text-destructive size-4 rounded-full"
                    />
                    To Be Deactivated
                  </div>
                  <Separator />
                  <div class="text-muted-foreground">
                    <span>
                      This user will be deactivated on
                      {{ formatDateTime(tenantUser.activeTo) }}.
                    </span>
                    <br /><br />
                    You can cancel the deactivation or reschedule the
                    deactivation and read more about user deactivation on the
                    <button
                      type="button"
                      class="text-link cursor-pointer"
                      @click="activeSection = 'danger-zone'"
                    >
                      danger zone
                    </button>
                    page.
                  </div>
                </div>
                <div
                  v-if="tenantUser.isTenantAdmin"
                  class="bg-background shadow-xs ring-border flex w-full shrink-0 grow-0 flex-col gap-2 self-start rounded-sm p-2.5 text-xs ring-1 ring-inset"
                >
                  <div class="flex flex-wrap items-center gap-2 font-medium">
                    <IconAdmin class="size-4 text-yellow-500" />
                    Tenant Admin
                  </div>
                  <Separator />
                  <div class="text-muted-foreground">
                    This user has tenant admin privileges and can manage all
                    users, settings, and data across the tenant.
                  </div>
                </div>
              </div>
            </SidebarContent>
          </Sidebar>
        </ResizablePanel>
        <ResizableHandle class="hidden md:flex" />
        <ResizablePanel class="flex flex-col" :default-size="78">
          <SidebarInset class="overflow-hidden">
            <div class="relative z-10 shrink-0">
              <div class="bg-background flex items-center px-3 py-1.5">
                <div class="min-w-0 flex-1">
                  <span
                    class="text-foreground truncate pl-1 text-sm font-semibold md:hidden"
                  >
                    {{ headerTitle }}
                  </span>
                </div>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent hidden shrink-0 cursor-pointer rounded-md p-1.5 transition-colors md:block"
                  @click="isFullScreen = !isFullScreen"
                >
                  <IconMinimize v-if="isFullScreen" class="size-4" />
                  <IconMaximize v-else class="size-4" />
                  <span class="sr-only">
                    {{ isFullScreen ? 'Exit full screen' : 'Full screen' }}
                  </span>
                </button>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer rounded-md p-1.5 transition-colors"
                  @click="open = false"
                >
                  <IconClose class="size-4" />
                  <span class="sr-only">Close</span>
                </button>
              </div>
              <nav
                class="border-border bg-background flex shrink-0 gap-1 overflow-x-auto border-b px-3 md:hidden"
              >
                <button
                  v-for="item in sidebarItems"
                  :key="item.key"
                  type="button"
                  :class="tabClass(item.key)"
                  @click="activeSection = item.key"
                >
                  <component :is="item.icon" class="size-4" />
                  <span>{{ item.label }}</span>
                </button>
              </nav>
              <div
                class="bg-background pointer-events-none h-3"
                style="
                  mask-image: linear-gradient(to bottom, black, transparent);
                "
              />
            </div>
            <div class="-mt-3 flex flex-1 flex-col gap-6 overflow-y-auto p-5">
              <TenantUserModalProvider v-if="tenantUser">
                <TenantUserOwnedDatabaseProvider>
                  <TenantUserDbAccessProvider
                    :tenant-user-id="tenantUser.userId"
                  >
                    <TenantUserSectionGeneralInfo
                      v-if="activeSection === 'general'"
                    />
                    <TenantUserSectionDatabaseAccess
                      v-else-if="activeSection === 'database-access'"
                    />
                    <TenantUserAccessRolesLayout
                      v-else-if="activeSection === 'access-roles'"
                      :id="tenantUser.userId"
                    >
                      <TenantUserAccessRolesView :id="tenantUser.userId" />
                    </TenantUserAccessRolesLayout>
                    <TenantUserDangerZoneView
                      v-else-if="activeSection === 'danger-zone'"
                      :id="tenantUser.userId"
                    />
                  </TenantUserDbAccessProvider>
                </TenantUserOwnedDatabaseProvider>
              </TenantUserModalProvider>
            </div>
          </SidebarInset>
        </ResizablePanel>
      </ResizablePanelGroup>
    </SidebarProvider>
  </Modal>
</template>
